import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { 
  TrendingUp, TrendingDown, DollarSign, Target, 
  Download, Filter, ArrowUpRight, ArrowDownRight,
  Building, BarChart3, PieChart, Activity
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, PieChart as RePieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const CompanyAnalytics = ({ subsidiaries = [] }) => {
  const [activeTab, setActiveTab] = useState('executive');
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('30');
  const [selectedSubsidiaries, setSelectedSubsidiaries] = useState('all');
  
  // Executive Summary Data
  const [executiveSummary, setExecutiveSummary] = useState(null);
  
  // Subsidiary Comparison Data
  const [subsidiaryComparison, setSubsidiaryComparison] = useState(null);
  const [comparisonMetric, setComparisonMetric] = useState('revenue');
  
  // Strategic Alignment Data
  const [strategicAlignment, setStrategicAlignment] = useState(null);
  
  // Variance Analysis Data
  const [varianceAnalysis, setVarianceAnalysis] = useState(null);
  
  // Trends & Forecasting Data
  const [trendsForecast, setTrendsForecast] = useState(null);
  
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (activeTab === 'executive') {
      fetchExecutiveSummary();
    } else if (activeTab === 'comparison') {
      fetchSubsidiaryComparison();
    } else if (activeTab === 'alignment') {
      fetchStrategicAlignment();
    } else if (activeTab === 'variance') {
      fetchVarianceAnalysis();
    } else if (activeTab === 'trends') {
      fetchTrendsForecasting();
    }
  }, [activeTab, timeRange, selectedSubsidiaries, comparisonMetric]);

  const fetchExecutiveSummary = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedSubsidiaries !== 'all') {
        params.append('subsidiary_ids', selectedSubsidiaries);
      }
      
      console.log('Fetching executive summary...');
      const response = await axios.get(`${API}/api/enterprise/analytics/executive-summary?${params}`);
      console.log('Executive summary response:', response.data);
      setExecutiveSummary(response.data);
    } catch (error) {
      console.error('Error fetching executive summary:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubsidiaryComparison = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/api/enterprise/analytics/subsidiary-comparison?metric=${comparisonMetric}`
      );
      setSubsidiaryComparison(response.data);
    } catch (error) {
      console.error('Error fetching subsidiary comparison:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStrategicAlignment = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedSubsidiaries !== 'all') {
        params.append('subsidiary_ids', selectedSubsidiaries);
      }
      
      const response = await axios.get(`${API}/api/enterprise/analytics/strategic-alignment?${params}`);
      setStrategicAlignment(response.data);
    } catch (error) {
      console.error('Error fetching strategic alignment:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVarianceAnalysis = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedSubsidiaries !== 'all') {
        params.append('subsidiary_ids', selectedSubsidiaries);
      }
      
      const response = await axios.get(`${API}/api/enterprise/analytics/variance-analysis?${params}`);
      setVarianceAnalysis(response.data);
    } catch (error) {
      console.error('Error fetching variance analysis:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrendsForecasting = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedSubsidiaries !== 'all') {
        params.append('subsidiary_ids', selectedSubsidiaries);
      }
      
      const response = await axios.get(`${API}/api/enterprise/analytics/trends-forecasting?${params}`);
      setTrendsForecast(response.data);
    } catch (error) {
      console.error('Error fetching trends and forecasting:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return '-';
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const exportData = async (format) => {
    // TODO: Implement export functionality
    console.log(`Exporting as ${format}`);
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <div className="space-y-6">
      {/* Header with Filters */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Company Analytics</h2>
          <p className="text-gray-600">Company-wide performance insights and comparisons</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => exportData('pdf')}>
            <Download className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportData('excel')}>
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Time Range</label>
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select time range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 Days</SelectItem>
                  <SelectItem value="30">Last 30 Days</SelectItem>
                  <SelectItem value="90">Last 90 Days</SelectItem>
                  <SelectItem value="365">Last Year</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Subsidiaries</label>
              <Select value={selectedSubsidiaries} onValueChange={setSelectedSubsidiaries}>
                <SelectTrigger>
                  <SelectValue placeholder="Select subsidiaries" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Subsidiaries</SelectItem>
                  {subsidiaries.map(sub => (
                    <SelectItem key={sub.id} value={sub.id}>{sub.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="executive">Executive Summary</TabsTrigger>
          <TabsTrigger value="comparison">Subsidiary Comparison</TabsTrigger>
          <TabsTrigger value="alignment">Strategic Alignment</TabsTrigger>
          <TabsTrigger value="variance">Variance Analysis</TabsTrigger>
          <TabsTrigger value="trends">Trends & Forecasting</TabsTrigger>
        </TabsList>

        {/* Executive Summary Tab */}
        <TabsContent value="executive" className="space-y-6">
          {loading ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-12">Loading executive summary...</div>
              </CardContent>
            </Card>
          ) : executiveSummary ? (
            <>
              {/* Key Metrics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Total Plans</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{executiveSummary.total_plans}</div>
                    <p className="text-xs text-gray-500 mt-1">
                      Across {executiveSummary.total_subsidiaries} subsidiaries
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Revenue</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatCurrency(executiveSummary.revenue.actual)}</div>
                    <div className="flex items-center mt-1">
                      {executiveSummary.revenue.variance >= 0 ? (
                        <ArrowUpRight className="w-4 h-4 text-green-600 mr-1" />
                      ) : (
                        <ArrowDownRight className="w-4 h-4 text-red-600 mr-1" />
                      )}
                      <span className={`text-xs font-medium ${executiveSummary.revenue.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatPercent(executiveSummary.revenue.variance)} vs target
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Costs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatCurrency(executiveSummary.costs.actual)}</div>
                    <div className="flex items-center mt-1">
                      {executiveSummary.costs.variance <= 0 ? (
                        <ArrowDownRight className="w-4 h-4 text-green-600 mr-1" />
                      ) : (
                        <ArrowUpRight className="w-4 h-4 text-red-600 mr-1" />
                      )}
                      <span className={`text-xs font-medium ${executiveSummary.costs.variance <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatPercent(executiveSummary.costs.variance)} vs budget
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Profitability</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatCurrency(executiveSummary.profitability.actual)}</div>
                    <div className="flex items-center mt-1">
                      {executiveSummary.profitability.variance >= 0 ? (
                        <ArrowUpRight className="w-4 h-4 text-green-600 mr-1" />
                      ) : (
                        <ArrowDownRight className="w-4 h-4 text-red-600 mr-1" />
                      )}
                      <span className={`text-xs font-medium ${executiveSummary.profitability.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatPercent(executiveSummary.profitability.variance)} vs target
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Objectives Progress */}
              <Card>
                <CardHeader>
                  <CardTitle>Strategic Objectives Progress</CardTitle>
                  <CardDescription>Company-wide completion rate</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Overall Progress</span>
                      <span className="text-sm font-medium">{executiveSummary.objectives_progress.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div
                        className="bg-blue-600 h-4 rounded-full"
                        style={{ width: `${executiveSummary.objectives_progress}%` }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Top Performers */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Top Performers</CardTitle>
                    <CardDescription>Best performing subsidiaries</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {executiveSummary.top_performers.map((perf, idx) => (
                        <div key={idx} className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                          <div>
                            <p className="font-medium">{perf.subsidiary_name}</p>
                            <p className="text-xs text-gray-600">{perf.plan_name}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-green-600">{formatPercent(perf.variance_percent)}</p>
                            <p className="text-xs text-gray-600">{formatCurrency(perf.actual_revenue)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Needs Attention</CardTitle>
                    <CardDescription>Underperforming subsidiaries</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {executiveSummary.underperformers.map((perf, idx) => (
                        <div key={idx} className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                          <div>
                            <p className="font-medium">{perf.subsidiary_name}</p>
                            <p className="text-xs text-gray-600">{perf.plan_name}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-red-600">{formatPercent(perf.variance_percent)}</p>
                            <p className="text-xs text-gray-600">{formatCurrency(perf.actual_revenue)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-12 text-gray-500">
                  No data available for the selected filters
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Subsidiary Comparison Tab */}
        <TabsContent value="comparison" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Subsidiary Performance Comparison</CardTitle>
                  <CardDescription>Compare metrics across subsidiaries</CardDescription>
                </div>
                <Select value={comparisonMetric} onValueChange={setComparisonMetric}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="revenue">Revenue</SelectItem>
                    <SelectItem value="costs">Costs</SelectItem>
                    <SelectItem value="profit">Profit</SelectItem>
                    <SelectItem value="variance">Variance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-12">Loading comparison data...</div>
              ) : subsidiaryComparison?.subsidiaries?.length > 0 ? (
                <>
                  {/* Bar Chart */}
                  <div className="h-80 mb-6">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={subsidiaryComparison.subsidiaries}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="subsidiary_name" angle={-45} textAnchor="end" height={80} />
                        <YAxis />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                        <Bar dataKey="metrics.revenue.actual" fill="#0088FE" name="Actual Revenue" />
                        <Bar dataKey="metrics.revenue.target" fill="#00C49F" name="Target Revenue" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Detailed Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium">Subsidiary</th>
                          <th className="px-4 py-3 text-left font-medium">Type</th>
                          <th className="px-4 py-3 text-center font-medium">Plans</th>
                          <th className="px-4 py-3 text-center font-medium">Team</th>
                          <th className="px-4 py-3 text-right font-medium">Revenue</th>
                          <th className="px-4 py-3 text-right font-medium">Costs</th>
                          <th className="px-4 py-3 text-right font-medium">Profit</th>
                          <th className="px-4 py-3 text-right font-medium">Variance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subsidiaryComparison.subsidiaries.map((sub, idx) => (
                          <tr key={idx} className="border-t hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium">{sub.subsidiary_name}</td>
                            <td className="px-4 py-3 text-gray-600">{sub.type}</td>
                            <td className="px-4 py-3 text-center">{sub.plans_count}</td>
                            <td className="px-4 py-3 text-center">{sub.team_count}</td>
                            <td className="px-4 py-3 text-right">{formatCurrency(sub.metrics.revenue.actual)}</td>
                            <td className="px-4 py-3 text-right">{formatCurrency(sub.metrics.costs.actual)}</td>
                            <td className="px-4 py-3 text-right font-medium">{formatCurrency(sub.metrics.profit.actual)}</td>
                            <td className="px-4 py-3 text-right">
                              <span className={`font-medium ${sub.metrics.revenue.variance_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatPercent(sub.metrics.revenue.variance_percent)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No subsidiary data available
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Strategic Alignment Tab */}
        <TabsContent value="alignment" className="space-y-6">
          {loading ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-12">Loading strategic alignment...</div>
              </CardContent>
            </Card>
          ) : strategicAlignment ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Total Objectives</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{strategicAlignment.total_objectives}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Completed</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">{strategicAlignment.completed_objectives}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">In Progress</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">{strategicAlignment.in_progress_objectives}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Objectives by Status */}
              <Card>
                <CardHeader>
                  <CardTitle>Objectives by Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <RePieChart>
                        <Pie
                          data={strategicAlignment.objectives_by_status}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry) => `${entry.status}: ${entry.count}`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="count"
                        >
                          {strategicAlignment.objectives_by_status.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </RePieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Subsidiary Alignment */}
              <Card>
                <CardHeader>
                  <CardTitle>Subsidiary Alignment</CardTitle>
                  <CardDescription>Objectives completion rate by subsidiary</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {strategicAlignment.subsidiary_alignment.map((sub, idx) => (
                      <div key={idx} className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="font-medium">{sub.subsidiary_name}</span>
                          <span className="text-sm text-gray-600">
                            {sub.completed_objectives}/{sub.total_objectives} ({sub.completion_rate.toFixed(1)}%)
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${sub.completion_rate}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Top Initiatives */}
              <Card>
                <CardHeader>
                  <CardTitle>Key Initiatives Progress</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium">Initiative</th>
                          <th className="px-4 py-3 text-left font-medium">Subsidiary</th>
                          <th className="px-4 py-3 text-left font-medium">Owner</th>
                          <th className="px-4 py-3 text-left font-medium">Status</th>
                          <th className="px-4 py-3 text-right font-medium">Progress</th>
                        </tr>
                      </thead>
                      <tbody>
                        {strategicAlignment.initiatives_progress.map((init, idx) => (
                          <tr key={idx} className="border-t hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium">{init.initiative_name}</td>
                            <td className="px-4 py-3 text-gray-600">{init.subsidiary_name}</td>
                            <td className="px-4 py-3 text-gray-600">{init.owner || 'Unassigned'}</td>
                            <td className="px-4 py-3">
                              <Badge variant={init.status === 'completed' ? 'default' : 'outline'}>
                                {init.status}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-right">{init.progress}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-12 text-gray-500">No alignment data available</div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Variance Analysis Tab */}
        <TabsContent value="variance" className="space-y-6">
          {loading ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-12">Loading variance analysis...</div>
              </CardContent>
            </Card>
          ) : varianceAnalysis ? (
            <>
              {/* Total Variance Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Total Company Variance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Amount</p>
                      <p className="text-3xl font-bold">{formatCurrency(varianceAnalysis.total_variance.amount)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Percentage</p>
                      <p className={`text-3xl font-bold ${varianceAnalysis.total_variance.percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatPercent(varianceAnalysis.total_variance.percent)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Variance Trends */}
              <Card>
                <CardHeader>
                  <CardTitle>Variance Trends</CardTitle>
                  <CardDescription>Monthly variance over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={varianceAnalysis.variance_trends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip formatter={(value) => formatPercent(value)} />
                        <Legend />
                        <Line type="monotone" dataKey="variance_percent" stroke="#8884d8" name="Variance %" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* High Variance Items */}
              <Card>
                <CardHeader>
                  <CardTitle>High Variance Items</CardTitle>
                  <CardDescription>Subsidiaries with variance &gt;20%</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {varianceAnalysis.high_variance_items.length > 0 ? (
                      varianceAnalysis.high_variance_items.map((item, idx) => (
                        <div key={idx} className={`p-3 rounded-lg ${item.variance_percent >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-medium">{item.subsidiary_name}</p>
                              <p className="text-xs text-gray-600">{item.plan_name}</p>
                            </div>
                            <div className="text-right">
                              <p className={`text-lg font-bold ${item.variance_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatPercent(item.variance_percent)}
                              </p>
                              <p className="text-xs text-gray-600">{formatCurrency(item.actual)} vs {formatCurrency(item.target)}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-gray-500 py-4">No high variance items found</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-12 text-gray-500">No variance data available</div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Trends & Forecasting Tab */}
        <TabsContent value="trends" className="space-y-6">
          {loading ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-12">Loading trends and forecasting...</div>
              </CardContent>
            </Card>
          ) : trendsForecast ? (
            <>
              {/* Growth Rate Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Company Growth Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold text-blue-600">
                    {formatPercent(trendsForecast.growth_rate)}
                  </div>
                  <p className="text-sm text-gray-600 mt-2">Average growth across all periods</p>
                </CardContent>
              </Card>

              {/* Revenue & Cost Trends */}
              <Card>
                <CardHeader>
                  <CardTitle>Revenue & Cost Trends</CardTitle>
                  <CardDescription>Historical performance over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendsForecast.revenue_trend.map((r, idx) => ({
                        month: r.month,
                        revenue: r.value,
                        costs: trendsForecast.cost_trend[idx]?.value || 0
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                        <Line type="monotone" dataKey="revenue" stroke="#0088FE" name="Revenue" strokeWidth={2} />
                        <Line type="monotone" dataKey="costs" stroke="#FF8042" name="Costs" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Forecast Next Quarter */}
              <Card>
                <CardHeader>
                  <CardTitle>Next Quarter Forecast</CardTitle>
                  <CardDescription>Projected performance based on current trends</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-2">Revenue</p>
                      <p className="text-2xl font-bold">{formatCurrency(trendsForecast.forecast_next_quarter.revenue)}</p>
                    </div>
                    <div className="p-4 bg-orange-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-2">Costs</p>
                      <p className="text-2xl font-bold">{formatCurrency(trendsForecast.forecast_next_quarter.costs)}</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-2">Profit</p>
                      <p className="text-2xl font-bold">{formatCurrency(trendsForecast.forecast_next_quarter.profit)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Performance Indicators */}
              <Card>
                <CardHeader>
                  <CardTitle>Performance Indicators</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {trendsForecast.performance_indicators.map((indicator, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center">
                          <span className="font-medium">{indicator.subsidiary_name}</span>
                          {indicator.trend === 'up' ? (
                            <TrendingUp className="w-4 h-4 text-green-600 ml-2" />
                          ) : (
                            <TrendingDown className="w-4 h-4 text-red-600 ml-2" />
                          )}
                        </div>
                        <span className="font-bold">{formatCurrency(indicator.revenue)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-12 text-gray-500">No trend data available</div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CompanyAnalytics;
